import { Injectable, OnDestroy } from '@angular/core';
import { Duration, DurationObjectUnits } from 'luxon';
import hash from 'object-hash';
import { HttpRequest } from '@angular/common/http';
import { EnvironmentService } from './environment.service';

type SaveMethod = 'localStorage' | 'sessionStorage';
export interface Caching {
  url: string;
  request: HttpRequest<any>;
  response: any;
  timestamp: number;
  timeToLive: DurationObjectUnits;
  saveMethod: SaveMethod;
}
const CACHE_TIME_CLEAN_INTERVAL_MS = 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SgCachingService implements OnDestroy {
  readonly storageKey = `cache${this.env.env === 'prod' ? '' : `-${this.env.env}`}`;
  private intervalCleaner: NodeJS.Timeout;
  cache: Map<string, Caching> = new Map<string, Caching>();

  constructor(private env: EnvironmentService) {
    this.intervalCleaner = setInterval(() => this.cacheCleanerByTime(), CACHE_TIME_CLEAN_INTERVAL_MS);
    this.loadCacheFromStorage('sessionStorage');
    this.loadCacheFromStorage('localStorage');
    this.saveCacheOnTabClosedHandler();
  }

  public getCache(url: string): Caching {
    const key = this.hashUrl(url);
    const cached = this.cache.get(key);
    if (!cached) {
      return null;
    }
    if (!this.isCacheActive(cached)) {
      this.cache.delete(key);
      return null;
    }
    return cached;
  }

  public setCache(caching: Partial<Caching>): void {
    const { url, response, timeToLive, saveMethod, request } = caching;
    const key = this.hashUrl(url);
    this.cache.set(key, {
      url,
      request,
      response,
      timestamp: Date.now(),
      timeToLive,
      saveMethod,
    });
  }

  public loadCacheFromStorage(saveMethod: SaveMethod): void {
    let cache: string;

    switch (saveMethod) {
      case 'sessionStorage':
        cache = sessionStorage.getItem(this.storageKey);
        break;
      case 'localStorage':
        cache = localStorage.getItem(this.storageKey);
        break;
    }

    if (cache) {
      const parsedCache: Caching = JSON.parse(cache);
      if (Array.isArray(parsedCache) && parsedCache.length > 0) {
        parsedCache.forEach((item) => {
          const key = item[0];
          const value = item[1];
          this.cache.set(key, value);
        });
      }
    }
  }

  public saveCacheToStorage(saveMethod: SaveMethod): void {
    const filteredMap = new Map([...this.cache].filter(([key, value]) => value.saveMethod === saveMethod));

    switch (saveMethod) {
      case 'localStorage':
        if (filteredMap.size === 0) {
          localStorage.removeItem(this.storageKey);
        } else {
          localStorage.setItem(this.storageKey, JSON.stringify(Array.from(filteredMap.entries())));
        }
        break;
      case 'sessionStorage':
        if (filteredMap.size === 0) {
          sessionStorage.removeItem(this.storageKey);
        } else {
          sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(filteredMap.entries())));
        }
        break;
    }
  }

  private hashUrl(url: string): string {
    return hash(url);
  }

  private cacheCleanerByTime() {
    this.cache.forEach((value, key) => {
      if (!this.isCacheActive(value)) {
        this.cache.delete(key);
      }
    });
  }

  private saveCacheOnTabClosedHandler() {
    window.addEventListener('beforeunload', (event) => {
      this.saveCacheToStorage('sessionStorage');
      this.saveCacheToStorage('localStorage');
    });
  }

  private isCacheActive(cached: Caching) {
    const now = Date.now();
    return cached.timestamp + Duration.fromObject(cached.timeToLive).toMillis() > now;
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalCleaner);
  }
}
